import { Component, Inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseDashboard } from '@memberjunction/ng-shared';
import { ResourceData } from '@memberjunction/core-entities';
import { BaseAGUAppSurfaceComponent } from 'agu_app_shell';
import { MAPS_HOST_ADAPTER, MapsLocation, MapsSessionService } from 'maps_ui';
import { ExplorerMapsHostAdapter } from './maps-explorer-host.adapter';

/**
 * MAPS / Science Talent Network, hosted inside MJExplorer as an "app surface".
 *
 * A thin BaseDashboard host (driver class 'MAPSAppSurface') around the shared UI
 * in packages/MAPSUI — the same components the standalone app serves. The
 * immersive body-class lifecycle, the visibility signal and the location
 * subscription all live in BaseAGUAppSurfaceComponent (packages/AGUAppShell),
 * shared with every other AGU surface; this file owns only what is MAPS-specific:
 * the isAdmin gate for the admin views and the admin landing. Navigation state
 * lives in ExplorerMapsHostAdapter (provided by this component); the shared
 * header drives it exactly as it drives the standalone app's router.
 *
 * Auth: the signed-in principal is an MJ user (staff SSO or a magic-link guest).
 * Every /maps-graphql request carries that user's MJ bearer (attached by the
 * module's Apollo auth link); the server resolves the MAPS session from it — no
 * cookie, no bridge handshake.
 */
@Component({
    standalone: false,
    selector: 'mj-maps-surface',
    templateUrl: './maps-surface.component.html',
    styleUrls: ['./maps-surface.component.scss'],
    /**
     * The host adapter is provided HERE, not on MapsSurfaceModule: that module is imported eagerly
     * into AppModule, so a module provider is a root singleton — one navigation state and one Router
     * listener for the whole session, shared by every MAPS tab. On this element injector each
     * surface gets its own, every maps_ui component under it resolves it by walking the element
     * tree, and it is destroyed with the surface. Pinned by maps-surface.providers.spec.ts.
     */
    providers: [{ provide: MAPS_HOST_ADAPTER, useClass: ExplorerMapsHostAdapter }],
})
@RegisterClass(BaseDashboard, 'MAPSAppSurface')
export class MapsSurfaceComponent extends BaseAGUAppSurfaceComponent<MapsLocation> {
    /** Admins get the admin views; recruiters never see them (fail closed). */
    isAdmin = false;
    override location: MapsLocation = 'search';

    protected override readonly Adapter: ExplorerMapsHostAdapter;

    constructor(
        private readonly session: MapsSessionService,
        @Inject(MAPS_HOST_ADAPTER) hostAdapter: ExplorerMapsHostAdapter
    ) {
        super();
        this.Adapter = hostAdapter;
    }

    async GetResourceDisplayName(_data: ResourceData): Promise<string> {
        return 'MAPS — Talent Search';
    }

    protected initDashboard(): void {
        // The MJ bearer is attached per-request by the Apollo auth link, so the
        // server resolves our session from it. We need the isAdmin flag to gate the
        // admin views; fail closed on error. Admins land on the dashboard first —
        // the same landing the standalone app gives an admin key.
        void this.session
            .Validate()
            .then((status) => {
                this.Zone.run(() => {
                    this.isAdmin = status.valid && status.isAdmin;
                    if (this.isAdmin && this.Adapter.CurrentLocation === 'search') {
                        this.Adapter.NavigateTo('admin-dashboard');
                    }
                    this.Cdr.detectChanges();
                });
            })
            .catch(() => {
                this.Zone.run(() => {
                    this.isAdmin = false; // fail closed — hide the admin views
                    this.Cdr.detectChanges();
                });
            });
    }

    protected loadData(): void {
        // Nothing to fetch at the surface level — the bearer flows on every
        // /maps-graphql call, so the embedded search-page works immediately.
    }
}

/** Imported for side effects from the module to prevent tree-shaking the registration. */
export function LoadMapsSurface(): void {
    // no-op; the @RegisterClass decorator runs on import
}
